/* eslint-disable react-hooks/rules-of-hooks */
import { Engine } from 'engine/Engine';
import { Facet } from 'engine/facets/Facet';
import { getDistance } from 'engine/lib/V2';
import { MAX_NEARBY_DISTANCE } from 'game/config';
import { fastMapValues } from 'game/lib/fastMapValues';
import { shallowEqual } from 'game/lib/shallowEqual';
import { Processor, ProcessorContext } from 'game/modules/Processor';
import { cpuProcessor } from 'game/modules/cpu/cpuProcessor';
import { doorProcessor } from 'game/modules/door/doorProcessor';
import { followProcessor } from 'game/modules/follow/followProcessor';
import { inventoryProcessor } from 'game/modules/inventory/inventoryProcessor';
import { logicProcessor } from 'game/modules/logic/logicProcessor';
import { moveToProcessor } from 'game/modules/move-to/moveToProcessor';
import { nearbyProcessor } from 'game/modules/nearby/nearbyProcessor';
import { scannerProcessor } from 'game/modules/scanner/scannerProcessor';
import { useProcessor } from 'game/modules/use/useProcessor';
import { AnyModule, BotState, Module, ModuleType, ProcessStatus } from 'game/state/BotState';

const processModule: Processor<Module> = (m, ctx) => {
  switch (m.type) {
    case ModuleType.Cpu: return cpuProcessor(m, ctx);
    case ModuleType.Inventory: return inventoryProcessor(m, ctx);
    case ModuleType.Logic: return logicProcessor(m, ctx);
    case ModuleType.Nearby: return nearbyProcessor(m, ctx);
    case ModuleType.Use: return useProcessor(m, ctx);
    case ModuleType.MoveTo: return moveToProcessor(m, ctx);
    case ModuleType.Follow: return followProcessor(m, ctx);
    case ModuleType.Scanner: return scannerProcessor(m, ctx);
    case ModuleType.Door: return doorProcessor(m, ctx);
  }
};

interface ProgrammableProps {
  state: BotState,
}

export class Programmable extends Facet<ProgrammableProps> {

  result: ProcessStatus | null = null;

  resolveInputs(module: AnyModule) {
    const { cache } = this.props.state;
    return fastMapValues(module.inputs, wire => cache[wire.moduleId]);
  }

  removeCachedResult(module: Module, recursive = false) {
    const { cache } = this.props.state;
    delete cache[module.id];
    if (!recursive) return;
    Object.values(module.inputs).forEach(wire => {
      const otherModule = this.props.state.modules.find(m => m.id === wire.moduleId);
      if (otherModule) this.removeCachedResult(otherModule);
    });
  }

  removeInvalidCache(module: Module) {
    const { cache } = this.props.state;
    Object.values(module.inputs).forEach(wire => {
      const otherModule = this.props.state.modules.find(m => m.id === wire.moduleId);
      if (otherModule) this.removeInvalidCache(otherModule);
    });
    const cachedResult = cache[module.id];
    if (!cachedResult) return;
    if (
      !shallowEqual(cachedResult.inputs, this.resolveInputs(module))
      || !shallowEqual(cachedResult.config, module.config)
    ) {
      const recursive = module.type === ModuleType.Logic;
      this.removeCachedResult(module, recursive);
    }

    // TODO: Probably should make this more scalable
    if (module.type === ModuleType.Nearby
      && cachedResult.result.status === 'success'
      && cachedResult.result.value.type === 'entity'
    ) {
      const d = getDistance(
        cachedResult.result.value.entity.transform.position,
        this.entity.transform.position,
      );
      if (d >= MAX_NEARBY_DISTANCE) {
        this.removeCachedResult(module);
      }
    }
  }

  processModule(module: Module, delta: number, engine: Engine): ProcessStatus {
    const processorContext: ProcessorContext = {
      bot: this,
      delta,
      engine,
      processOther: (m, ctx) => {
        const cachedResult = this.props.state.cache[m.id];
        if (m.type !== ModuleType.Nearby && cachedResult) {
          if (cachedResult.result.status === 'success' || cachedResult.result.status === 'error') {
            return cachedResult.result;
          }
        }
        const result = processModule(m, ctx);
        this.props.state.cache[m.id] = {
          result,
          inputs: this.resolveInputs(m),
          config: m.config ? { ...m.config } : null,
        };
        return result;
      },
    };

    return processorContext.processOther(module, processorContext);
  }

  step(delta: number, engine: Engine): void {
    const { state } = this.props;
    if (!state.running || state.didDie) return;
    const cpu = state.modules.find(m => m.type === ModuleType.Cpu);
    if (!cpu) return;

    this.removeInvalidCache(cpu);

    this.result = this.processModule(cpu, delta, engine);
    if (this.result.status !== 'running') {
      this.props.state.cache = {};
      if (!this.props.state.looping) {
        this.props.state.running = false;
      }
    }
  }
}
